import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { Card } from "@/components/ui/card";
import { StatusBadge, eur, fmtDate } from "../helpers";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CalendarArrowDown, CalendarArrowUp, Users, Sparkles, Wrench, CreditCard, FileWarning, TrendingUp, Percent, DollarSign, BedDouble, PiggyBank, Bell } from "lucide-react";
import TodayBriefCard from "../components/TodayBriefCard";

function KpiCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <Card data-testid={testid} className="flex items-center gap-4 border-border p-5 transition-all hover:-translate-y-1 hover:shadow-md">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={20} /></div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-slate-900 font-head">{value}</div>
        <div className="truncate text-xs text-slate-500">{label}</div>
      </div>
    </Card>
  );
}

const NOTIF_STYLE = {
  danger: "border-l-rose-500 bg-rose-50", warning: "border-l-amber-500 bg-amber-50", info: "border-l-blue-500 bg-blue-50",
};

export default function Dashboard() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [apts, setApts] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/apartments/lookup").then((r) => setApts(r.data));
  }, []);

  const aptName = (id) => apts.find((a) => a.id === id)?.name || "—";
  if (!d) return <div className="text-slate-400">…</div>;

  const kpis = [
    { icon: CalendarArrowDown, label: t("arrivalsToday"), value: d.arrivals_today, tone: "bg-blue-100 text-blue-700", testid: "kpi-arrivals" },
    { icon: CalendarArrowUp, label: t("departuresToday"), value: d.departures_today, tone: "bg-indigo-100 text-indigo-700", testid: "kpi-departures" },
    { icon: Users, label: t("presentGuests"), value: d.current_guests, tone: "bg-violet-100 text-violet-700", testid: "kpi-present-guests" },
    { icon: Sparkles, label: t("cleaningsTodo"), value: d.cleanings_todo, tone: "bg-cyan-100 text-cyan-700", testid: "kpi-cleanings" },
    { icon: Wrench, label: t("openMaintenance"), value: d.open_maintenance, tone: "bg-amber-100 text-amber-800", testid: "kpi-maintenance" },
    { icon: CreditCard, label: t("pendingPayments"), value: d.pending_payments, tone: "bg-rose-100 text-rose-700", testid: "kpi-payments" },
    { icon: Percent, label: t("monthlyOccupancy"), value: d.monthly_occupancy + "%", tone: "bg-emerald-100 text-emerald-700", testid: "kpi-occupancy" },
    { icon: TrendingUp, label: t("adr"), value: eur(d.adr), tone: "bg-teal-100 text-teal-700", testid: "kpi-adr" },
    { icon: BedDouble, label: t("revpar"), value: eur(d.revpar), tone: "bg-sky-100 text-sky-700", testid: "kpi-revpar" },
    { icon: DollarSign, label: t("revenueMonth"), value: eur(d.monthly_revenue), tone: "bg-primary text-primary-foreground", testid: "kpi-revenue" },
    { icon: PiggyBank, label: t("operatingMargin2"), value: eur(d.operating_margin), tone: d.operating_margin >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700", testid: "kpi-margin" },
  ];

  const notifText = (n) => {
    if (n.type === "maintenance") return `${t("urgentMaintenance")}: ${n.text}`;
    if (n.type === "cleaning") return `${n.text} ${t("cleaningsToday")}`;
    if (n.type === "payment") return `${n.text} ${t("paymentsToCollect")}`;
    if (n.type === "document") return `${n.text} ${t("docsToCollect")}`;
    return n.text;
  };

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
        {kpis.map((k) => <KpiCard key={k.testid} {...k} />)}
      </div>

      <TodayBriefCard />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border p-6 lg:col-span-2" data-testid="trend-chart">
          <h3 className="mb-6 font-head text-lg font-semibold text-slate-900">{t("trendTitle")}</h3>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.monthly_trend} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1B3A5B" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#1B3A5B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e0d4" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => n === "revenue" ? eur(v) : v + "%"} contentStyle={{ borderRadius: 8, border: "1px solid #e7e0d4", fontSize: 13 }} />
                <Area type="monotone" dataKey="revenue" stroke="#1B3A5B" strokeWidth={2} fill="url(#rev)" name="revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="border-border p-6" data-testid="notifications-panel">
          <div className="mb-4 flex items-center gap-2">
            <Bell size={18} className="text-primary" />
            <h3 className="font-head text-lg font-semibold text-slate-900">{t("notifications")}</h3>
          </div>
          {d.notifications.length === 0 ? (
            <p className="text-sm text-slate-400">{t("noNotifications")}</p>
          ) : (
            <ul className="space-y-2">
              {d.notifications.map((n, i) => (
                <li key={i} data-testid={`notif-${n.type}-${i}`} className={`rounded-md border-l-4 px-3 py-2.5 text-sm text-slate-700 ${NOTIF_STYLE[n.level] || "border-l-slate-400 bg-slate-50"}`}>
                  {notifText(n)}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-border p-6" data-testid="arrivals-list">
          <h3 className="mb-4 font-head text-lg font-semibold text-slate-900">{t("todayArrivals")}</h3>
          {d.arrivals.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
            <ul className="divide-y divide-slate-100">
              {d.arrivals.map((b) => (
                <li key={b.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-slate-900">{b.guest_name}</div>
                    <div className="text-xs text-slate-500">{aptName(b.apartment_id)} · {b.guests} {t("guests").toLowerCase()}</div>
                  </div>
                  <StatusBadge value={b.payment_status} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="border-border p-6" data-testid="departures-list">
          <h3 className="mb-4 font-head text-lg font-semibold text-slate-900">{t("todayDepartures")}</h3>
          {d.departures.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
            <ul className="divide-y divide-slate-100">
              {d.departures.map((b) => (
                <li key={b.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-slate-900">{b.guest_name}</div>
                    <div className="text-xs text-slate-500">{aptName(b.apartment_id)} · {fmtDate(b.check_out)}</div>
                  </div>
                  <StatusBadge value={b.document_status} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
